import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import { departmentsService } from '@/lib/api/services';
import type {
  CreateDepartmentInput,
  UpdateDepartmentInput,
} from '@coi-live/shared-types';

export function useDepartments(params: { limit?: number; cursor?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.departments.list(params as Record<string, unknown>),
    queryFn: () => departmentsService.list(params),
  });
}

export function useDepartment(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.departments.detail(id ?? ''),
    queryFn: () => departmentsService.get(id!),
    enabled: !!id,
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDepartmentInput) => departmentsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.lists() });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDepartmentInput }) =>
      departmentsService.update(id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.departments.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.lists() });
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => departmentsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.lists() });
    },
  });
}
